import {
  forEach,
  includes,
  isArray,
  isEmpty,
  keys,
  map,
  omit,
  reduce,
  toPairs,
} from 'lodash/fp'
import { createActions } from 'redux-actions'
import * as v from 'valibot'

import { mediaSummaryFilterSelector } from '../selectors/mediaSummary.selectors'

const mediaSummaryFiltersSchema = v.partial(
  v.object({
    search: v.string(),
  }),
)

const eventsSchema = v.partial(
  v.object({
    owner: v.union([v.literal('me')]),
    type: v.array(v.union([v.literal('in_person'), v.literal('online')])),
    user: v.custom((val) =>
      typeof val === 'string' ? /^.+:.+$/.test(val) : false,
    ),
    state: v.union([
      v.literal('upcoming'),
      v.literal('overdue'),
      v.literal('unassigned'),
      v.literal('active'),
      v.literal('in_progress'),
    ]),
    group: v.custom((val) =>
      typeof val === 'string' ? /^.+:.+$/.test(val) : false,
    ),
  }),
)

const learnpathsSchema = v.partial(
  v.object({
    language: v.string(),
    owner: v.string(),
    types: v.array(
      v.union([
        v.literal('mandatory'),
        v.literal('linear'),
        v.literal('certificate'),
      ]),
    ),
    user: v.custom((val) =>
      typeof val === 'string' ? /^.+:.+$/.test(val) : false,
    ),
    status: v.union([
      v.literal('active'),
      v.literal('draft'),
      v.literal('inactive'),
    ]),
    group: v.custom((val) =>
      typeof val === 'string' ? /^.+:.+$/.test(val) : false,
    ),
  }),
)

const paramsSchema = v.object({
  ...eventsSchema.entries,
  ...mediaSummaryFiltersSchema.entries,
  ...learnpathsSchema.entries,
})

const setNewSearch = (searchParams, history) => {
  if (searchParams.size === 0) {
    history.push(history.location.pathname)
  } else {
    history.push(`${history.location.pathname}?${searchParams.toString()}`)
  }
}

const isArrayFilter = (key) =>
  paramsSchema.entries[key].wrapped.type === 'array'

// Function sanitizes search parameters against existing filter schema. Faulty URL
// entries are ignored. Valibot is used to validate the schema.
const sanitizeParams = (searchParams: URLSearchParams) => {
  const raw = reduce(
    (acc, key) => {
      const value = searchParams.get(key)
      if (value === null) return acc
      if (isArrayFilter(key)) {
        try {
          const parsed = JSON.parse(value)
          return Array.isArray(parsed) ? { ...acc, [key]: parsed } : acc
        } catch {
          return acc
        }
      }
      return { ...acc, [key]: value }
    },
    {},
    keys(paramsSchema.entries),
  )

  const parsedFilter = v.safeParse(paramsSchema, raw)

  if (parsedFilter.success) {
    return parsedFilter.output
  }

  // @ts-ignore
  return omit(map('path[0].key', parsedFilter.issues), parsedFilter.output)
}

const actions = {
  mediaSummary: createActions(
    'CLEAR_SELECTED_ROWS',
    'INCREMENT_DIALOG_ITEMS_FINISHED',
    'REMOVE_ROW_SELECTION',
    'RESET',
    'SET_DETAILS_ANIMATION_MEDIA_ID',
    'SET_DIALOG',
    'SET_FILTERS',
    'SET_MEDIA_ID',
    'SET_PAGE_INDEX',
    'SET_PAGE_SIZE',
    'SET_PAGE_SORTING',
    'SET_SHOW_ONLY_SELECTED',
    'SET_TAB',
    'TOGGLE_ROW_SELECTION',
    'TOGGLE_SHOW_ONLY_SELECTED',
  ),
}

const setFilters = async (dispatch, history, searchParams) => {
  setNewSearch(searchParams, history)
  return Promise.all([
    dispatch(actions.mediaSummary.setFilters(sanitizeParams(searchParams))),
    dispatch(actions.mediaSummary.setPageIndex(0)),
  ])
}

actions.mediaSummary.initialize =
  (initialState, history) => async (dispatch) => {
    const searchParams = new URLSearchParams(history.location.search)
    await dispatch(actions.mediaSummary.reset(initialState ?? {}))

    if (!isEmpty(initialState?.filters)) {
      forEach(([filter, newValue]) => {
        if (isArray(newValue)) {
          searchParams.set(filter, JSON.stringify(newValue))
        } else {
          searchParams.set(filter, String(newValue))
        }
      }, toPairs(initialState.filters))

      return setFilters(dispatch, history, searchParams)
    }

    return Promise.resolve()
  }

actions.mediaSummary.appendFilter =
  (key, newValue, history) => (dispatch, getState) => {
    const searchParams = new URLSearchParams(history.location.search)
    // @ts-ignore
    const currentFilters = mediaSummaryFilterSelector(getState(), {
      filter: key,
    })

    if (!currentFilters || isArray(currentFilters)) {
      const newArray = isArray(currentFilters)
        ? currentFilters.concat(newValue)
        : [newValue]
      searchParams.set(key, JSON.stringify(newArray))
      setFilters(dispatch, history, searchParams)
    }
  }

actions.mediaSummary.updateFilter = (key, newValue, history) => (dispatch) => {
  const searchParams = new URLSearchParams(history.location.search)

  if (isArray(newValue)) {
    searchParams.set(key, JSON.stringify(newValue))
  } else {
    searchParams.set(key, newValue)
  }

  setFilters(dispatch, history, searchParams)
}

actions.mediaSummary.removeFilter =
  (key, oldValue, history) => (dispatch, getState) => {
    const searchParams = new URLSearchParams(history.location.search)
    // @ts-ignore
    const values = mediaSummaryFilterSelector(getState(), { filter: key })
    if (isArray(values) && oldValue) {
      const newArray = values.filter((item) => item !== oldValue)

      if (isEmpty(newArray)) {
        searchParams.delete(key)
      } else {
        searchParams.set(key, JSON.stringify(newArray))
      }
    } else {
      searchParams.delete(key)
    }

    setFilters(dispatch, history, searchParams)
  }

actions.mediaSummary.toggleFilter =
  (key, value, history) => (dispatch, getState) => {
    const searchParams = new URLSearchParams(history.location.search)
    // @ts-ignore
    const values = mediaSummaryFilterSelector(getState(), { filter: key })
    if (isArray(values)) {
      const newArray = includes(value, values)
        ? values.filter((item) => item !== value)
        : values.concat(value)

      if (isEmpty(newArray)) {
        searchParams.delete(key)
      } else {
        searchParams.set(key, JSON.stringify(newArray))
      }
    } else if (searchParams.has(key)) {
      searchParams.delete(key)
    } else if (isArrayFilter(key)) {
      searchParams.set(key, JSON.stringify([value]))
    } else {
      searchParams.set(key, value)
    }

    setFilters(dispatch, history, searchParams)
  }

actions.mediaSummary.clearFilters = (history) => (dispatch) => {
  setFilters(dispatch, history, new URLSearchParams())
}

export default actions.mediaSummary
